import {
  Button,
  Card,
  Col,
  Create,
  CreateButton,
  Divider,
  Form,
  Icons,
  Input,
  notification,
  Row,
  Select,
  Space,
  Table,
  Typography,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { CustomDrawer, SingleDatePicker } from 'components';
import { AddProductForm } from 'components/stock-transfer/AddProductForm';
import { EditProductForm } from 'components/stock-transfer/EditProductForm';
import dayjs, { Dayjs } from 'dayjs';
import { ETransferStatus } from 'enums';
import { ITransferStock } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { createTransferStock, CreateTransferStockData } from 'services/stock-transfer';
import { getTransactionToken } from 'services/transaction';
import { IVariant } from 'services/variant';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';

export type ITransferProductState = IVariant & { quantity: number };

const { Option } = Select;
const { Title } = Typography;

export const TransferStockCreate: React.FC = () => {
  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stockTransferDate, setStockTransferDate] = useState<Date>();
  const [addedProducts, setAddedProducts] = useState<ITransferProductState[]>([]);
  const [editTransferProduct, setEditTransferProduct] = useState<ITransferProductState | null>(
    null
  );
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [fromWarehouseId, setFromWarehouseId] = useState<string | undefined>();
  const [toWarehouseId, setToWarehouseId] = useState<string | undefined>();
  const transactionTokenRef = useRef<string>();
  const transactionTokenRequestRef = useRef<boolean>(false);
  const { list } = useNavigation();
  const { form, formProps, saveButtonProps } = useForm();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const warehousesResponse = await getWarehousesDropdown();
        const warehouses = warehousesResponse.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));

        setWarehouses(warehouses);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
        setWarehouses([]);
      }
      setIsLoading(false);
    })();
  }, []);

  const onTransferFromWarehouseChange = async (id: string) => {
    setFromWarehouseId(id);
  };

  const onTransferToWarehouseChange = async (id: string) => {
    setToWarehouseId(id);
  };

  const onSubmitEditProduct = (editedProduct: ITransferProductState) => {
    const products = addedProducts.map((product) => {
      if (product.productVariantId === editedProduct.productVariantId) {
        return { ...editedProduct };
      }
      return { ...product };
    });
    setAddedProducts(products);
    setIsEditDrawerVisible(false);
  };

  const getOptions = (items: { id: string; name: string }[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onFinish = async (values: any) => {
    if (!fromWarehouseId || !toWarehouseId) return;

    setIsSubmitting(true);
    if (toWarehouseId === fromWarehouseId) {
      form.setFields([
        {
          name: 'transferToWarehouse',
          errors: [`Already selected in From Warehouse`],
        },
        {
          name: 'transferFromWarehouse',
          errors: [`Already selected in To Warehouse`],
        },
      ]);
      setIsSubmitting(false);
      return;
    }
    if (!transactionTokenRef.current && transactionTokenRequestRef.current === false) {
      transactionTokenRequestRef.current = true;
      transactionTokenRef.current = await getTransactionToken();
    }
    const data: CreateTransferStockData = {
      fromWarehouse: fromWarehouseId,
      toWarehouse: toWarehouseId,
      transferStatus: ETransferStatus.IN_TRANSIT,
      transferStockProducts: addedProducts.map((product) => ({
        productVariant: product.productVariantId,
        quantity: product.quantity,
        unitPrice: product.sellingPrice,
      })),
      transferDate: stockTransferDate
        ? new Date(stockTransferDate).toUTCString()
        : new Date().toUTCString(),
      note: values.note ?? null,
      transactionToken: transactionTokenRef.current!,
    };

    try {
      const result = await createTransferStock(data);
      const createdTransfer = result?.createTransferStockCopy as ITransferStock | undefined;
      const isPending = createdTransfer?.transferStatus === ETransferStatus.PENDING_APPROVAL;
      notification.success({
        message: 'Success',
        description: isPending
          ? 'Transfer request submitted — awaiting main-warehouse approval.'
          : 'Successfully created a transfer.',
      });
      list('stock-transfers');
    } catch (err: any) {
      transactionTokenRef.current = undefined;
      transactionTokenRequestRef.current = false;
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsSubmitting(false);
  };

  const fromWarehouseOptions = getOptions(warehouses);
  const toWarehouseOptions = getOptions(
    warehouses.filter((warehouse) => warehouse.id !== fromWarehouseId)
  );

  const cardTitle = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
        whiteSpace: 'normal',
      }}
    >
      <Title level={4} style={{ margin: 0 }}>
        Add product
      </Title>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <CreateButton type="primary" onClick={() => setIsAddDrawerVisible(true)}>
          Add New
        </CreateButton>
      </div>
    </div>
  );

  return (
    <>
      {/* Add Drawer Form */}
      <CustomDrawer
        title="Add Product"
        visible={isAddDrawerVisible}
        onClose={() => setIsAddDrawerVisible(false)}
      >
        <AddProductForm
          warehouseId={fromWarehouseId}
          transferProducts={addedProducts}
          onSubmit={(product: ITransferProductState) =>
            setAddedProducts((prevProducts) => [...prevProducts, product])
          }
        />
      </CustomDrawer>
      {/* Edit Drawer Form */}
      <CustomDrawer
        title="Edit Product"
        visible={isEditDrawerVisible}
        onClose={() => setIsEditDrawerVisible(false)}
      >
        <EditProductForm
          editProduct={editTransferProduct!}
          warehouse={fromWarehouseId}
          onSubmit={onSubmitEditProduct}
        />
      </CustomDrawer>

      <Create
        isLoading={isLoading}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !addedProducts.length,
          loading: isSubmitting,
        }}
      >
        <Form {...formProps} form={form} layout="vertical" onFinish={onFinish}>
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card title="Transfer Information">
                <Form.Item
                  label="From Warehouse"
                  name="transferFromWarehouse"
                  rules={[{ required: true, message: 'Please select from warehouse' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select a warehouse"
                    disabled={!warehouses.length}
                    onChange={onTransferFromWarehouseChange}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {fromWarehouseOptions}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="To Warehouse"
                  name="transferToWarehouse"
                  rules={[{ required: true, message: 'Please select to warehouse' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select a warehouse"
                    disabled={!fromWarehouseId}
                    onChange={onTransferToWarehouseChange}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {toWarehouseOptions}
                  </Select>
                </Form.Item>
                <Form.Item label="Date" name="stockTransferDate">
                  <SingleDatePicker
                    value={dayjs(stockTransferDate)}
                    onChange={(value: Dayjs) => setStockTransferDate(new Date(value.toDate()))}
                  />
                </Form.Item>
                <Form.Item label="Note" name="note">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Form>

        <Divider />

        <Card title={cardTitle}>
          <Table dataSource={addedProducts} rowKey="sku" scroll={{ x: true }}>
            <Table.Column dataIndex="sku" title="SKU" />
            <Table.Column dataIndex="productVariantName" title="Variant Name" />
            <Table.Column dataIndex="quantity" title="Quantity" />
            <Table.Column dataIndex="sellingPrice" title="Unit Price" />
            <Table.Column<ITransferProductState>
              title="Actions"
              dataIndex="actions"
              render={(_text, record): React.ReactNode => {
                return (
                  <Space>
                    <Button
                      size="small"
                      icon={<Icons.EditOutlined />}
                      onClick={() => {
                        setIsEditDrawerVisible(true);
                        setEditTransferProduct(record);
                      }}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<Icons.DeleteOutlined />}
                      onClick={() => {
                        const filteredAddedProducts = addedProducts.filter(
                          (product) => product.productVariantId !== record.productVariantId
                        );
                        setAddedProducts(filteredAddedProducts);
                      }}
                    />
                  </Space>
                );
              }}
            />
          </Table>
        </Card>
      </Create>
    </>
  );
};
